import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import type { ToastData } from "./Toast.types";

/**
 * Array of toast IDs in order of appearance (for stacking)
 */
export const toastIdsAtom = atom<string[]>([]);

/**
 * Atom family for individual toast data
 */
export const toastAtomFamily = atomFamily((id: string) =>
  atom<ToastData | null>(null)
);

/**
 * Derived atom to get all active toasts
 */
export const allToastsAtom = atom((get) => {
  const ids = get(toastIdsAtom);
  return ids
    .map((id) => {
      const toast = get(toastAtomFamily(id));
      return toast ? { ...toast, id } : null;
    })
    .filter((toast): toast is { id: string } & ToastData => toast !== null);
});

/**
 * Action: Add a new toast
 */
export const addToastAtom = atom(
  null,
  (get, set, toastData: Omit<ToastData, "id">) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: ToastData = {
      ...toastData,
      id,
    };
    set(toastAtomFamily(id), newToast);
    set(toastIdsAtom, (prev) => [...prev, id]);
    return id;
  }
);

/**
 * Action: Remove a toast
 */
export const removeToastAtom = atom(null, (get, set, id: string) => {
  set(toastAtomFamily(id), null);
  set(toastIdsAtom, (prev) => prev.filter((toastId) => toastId !== id));
});

/**
 * Action: Update a toast
 */
export const updateToastAtom = atom(
  null,
  (get, set, id: string, updates: Partial<ToastData>) => {
    const current = get(toastAtomFamily(id));
    if (current) {
      set(toastAtomFamily(id), { ...current, ...updates });
    }
  }
);

